import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { sortable } from 'react-sortable';

export const addonShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  name: PropTypes.string,
  category_label: PropTypes.string,
  image_url: PropTypes.string,
  is_senior: PropTypes.bool,
  is_elementary: PropTypes.bool,
  is_holiday: PropTypes.bool,
  price_with: PropTypes.number,
  price_without: PropTypes.number,
  status: PropTypes.string,
  edit_path: PropTypes.string,
  show_path: PropTypes.string,
});

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

export const formatPrice = (value) => (value == null ? '—' : currency.format(value));

// The lifecycle column, which replaced "all three audience flags false" as the
// way to say an add-on is not for sale. The flags now only answer WHERE it
// sells, so a withdrawn product keeps that record instead of having it erased.
export const STATUS_LABELS = {
  active: 'Active',
  draft: 'Draft',
  hidden: 'Hidden',
};

export const statusOf = (item) => (STATUS_LABELS[item.status] ? item.status : 'draft');

export const isSoldNowhere = (item) => !item.is_senior && !item.is_elementary && !item.is_holiday;

const GripIcon = () => (
  <svg width="10" height="16" viewBox="0 0 10 16" fill="currentColor" aria-hidden="true" focusable="false">
    <circle cx="2" cy="3" r="1.4" />
    <circle cx="8" cy="3" r="1.4" />
    <circle cx="2" cy="8" r="1.4" />
    <circle cx="8" cy="8" r="1.4" />
    <circle cx="2" cy="13" r="1.4" />
    <circle cx="8" cy="13" r="1.4" />
  </svg>
);

const PlaceholderIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    aria-hidden="true"
    focusable="false"
  >
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <circle cx="8.5" cy="9.5" r="1.6" />
    <path d="M21 16l-5-5-5 5-3-3-5 5" />
  </svg>
);

export const DRAG_DISABLED_HINT = 'Reordering works on the Active tab, in Store order, with an empty search — '
  + 'dragging a filtered or sorted list would scramble the order customers see.';

// One row of the add-ons table.
//
// The drag props come from react-sortable's `sortable()` HOC, which spreads them
// onto whatever this renders. Two deliberate changes to what it hands us:
//
//   * `draggable` is OUR state, not the HOC's hardcoded `true`. The row becomes
//     draggable only while the pointer is on the grip, so selecting a name or
//     dragging text out of a cell doesn't start a reorder.
//   * the HOC also passes `items` / `moveInMiddle`, which are not DOM
//     attributes; they are pulled out here so React doesn't warn about them.
export const AddonRow = (props) => {
  const {
    item, selected, onToggleSelect, dragEnabled, menuOpen, onToggleMenu, onDelete,
    onDragStart, onDragEnd, onTouchStart,
    draggable, items, onSortItems, moveInMiddle, sortId,
    ...rowProps
  } = props;

  // ⚠️ THE ROW IS DRAGGABLE UP FRONT, and the handle is NOT a <button>.
  //
  // It used to become draggable on the handle's mousedown, which is two bets at
  // once: that React has flushed the attribute before Chrome decides whether a
  // drag is starting, and that a gesture beginning on a FORM CONTROL can start
  // one at all. The second is simply false -- Chrome does not begin a drag when
  // the press lands on a button, input or select -- so the handle has to be a
  // span that only looks and reads like a button.
  //
  // Dragging is still handle-only: a press anywhere else cancels the drag in
  // onDragStart below.
  const fromHandle = useRef(false);

  const status = statusOf(item);
  const saves = item.price_with != null && item.price_without != null && item.price_without > item.price_with
    ? item.price_without - item.price_with
    : null;

  return (
    <div
      className={`aa-grid aa-row${selected ? ' is-selected' : ''}`}
      draggable={dragEnabled}
      onMouseDown={(event) => { fromHandle.current = Boolean(event.target.closest('.aa-handle')); }}
      onDragStart={(event) => {
        if (!fromHandle.current) { event.preventDefault(); return; }
        if (onDragStart) onDragStart(event);
      }}
      onDragEnd={(event) => { fromHandle.current = false; if (onDragEnd) onDragEnd(event); }}
      {...rowProps}
    >
      <div>
        <input
          type="checkbox"
          className="aa-checkbox"
          checked={selected}
          onChange={() => onToggleSelect(item.id)}
          aria-label={`Select ${item.name}`}
        />
      </div>

      <div>
        <span
          className={`aa-handle${dragEnabled ? '' : ' is-disabled'}`}
          role="button"
          tabIndex={-1}
          aria-label={dragEnabled ? `Reorder ${item.name}` : 'Reordering unavailable'}
          aria-disabled={!dragEnabled}
          title={dragEnabled ? 'Drag to change the store order' : DRAG_DISABLED_HINT}
          data-id={sortId}
          onTouchStart={onTouchStart}
        >
          <GripIcon />
        </span>
      </div>

      <div className="aa-product">
        {item.image_url ? (
          <img className="aa-thumb" src={item.image_url} alt="" loading="lazy" />
        ) : (
          <div className="aa-thumb aa-thumb--empty" aria-hidden="true"><PlaceholderIcon /></div>
        )}

        <div className="aa-names">
          <a className="aa-name" href={item.edit_path} title={item.name}>{item.name}</a>
          {item.category_label ? <span className="aa-category">{item.category_label}</span> : null}
        </div>
      </div>

      <div>
        <span className={`aa-pill aa-pill--${status}`}>{STATUS_LABELS[status]}</span>
      </div>

      <div>
        {isSoldNowhere(item) ? <span className="aa-none">Not in any store</span> : (
          <div className="aa-tags">
            {item.is_senior ? <span className="aa-tag aa-tag--seniors">Seniors</span> : null}
            {item.is_elementary ? <span className="aa-tag aa-tag--elementary">Elementary</span> : null}
            {item.is_holiday ? <span className="aa-tag aa-tag--gifts">Gifts</span> : null}
          </div>
        )}
      </div>

      <div>
        <span className="aa-price">{formatPrice(item.price_with)}</span>
        {saves ? <span className="aa-saves">{`Saves ${currency.format(saves)}`}</span> : null}
      </div>

      <div><span className="aa-price">{formatPrice(item.price_without)}</span></div>

      <div className="aa-actions">
        <button
          type="button"
          className="aa-kebab"
          aria-label={`Actions for ${item.name}`}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={() => onToggleMenu(item.id)}
        >
          ⋯
        </button>

        {menuOpen ? (
          <div className="aa-menu" role="menu">
            <a role="menuitem" href={item.edit_path}>Edit</a>
            <a role="menuitem" href={item.show_path}>View</a>
            <button type="button" role="menuitem" className="aa-menu-danger" onClick={() => onDelete(item)}>
              Delete
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

AddonRow.propTypes = {
  item: addonShape.isRequired,
  selected: PropTypes.bool,
  onToggleSelect: PropTypes.func.isRequired,
  dragEnabled: PropTypes.bool,
  menuOpen: PropTypes.bool,
  onToggleMenu: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onDragStart: PropTypes.func,
  onDragEnd: PropTypes.func,
  onTouchStart: PropTypes.func,
  draggable: PropTypes.bool,
  items: PropTypes.arrayOf(addonShape),
  onSortItems: PropTypes.func,
  moveInMiddle: PropTypes.bool,
  sortId: PropTypes.number,
};

AddonRow.defaultProps = {
  selected: false,
  dragEnabled: false,
  menuOpen: false,
  onDragStart: undefined,
  onDragEnd: undefined,
  onTouchStart: undefined,
  draggable: undefined,
  items: undefined,
  onSortItems: undefined,
  moveInMiddle: undefined,
  sortId: undefined,
};

// The row as rendered when dragging is off (a filtered tab, a search, or any
// sort other than Store order). Same markup, no drag wiring at all, so a drag
// cannot fight the view being shown.
export const StaticRow = AddonRow;

export default sortable(AddonRow);
